/**
 * MotifMenuOptions.js
 * Created by calhosh on 7/7/2021.
 */
import React, {useMemo, useCallback} from 'react';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import MenuItem from '@ey/motif-react-wrapper/MenuItem';
import MenuItemGroup from '@ey/motif-react-wrapper/MenuItemGroup';
import {labels} from '../../../util/utils';
import env from '../../../util/env';
import {documentTypes, smartScreenRoutes, gaRoleTypes} from '../../../util/uiconstants';
import {useAresTaskBuildMilestoneId} from '../../../util/customhooks';
import EllipseControl from '../../Common/EllipsesControl/EllipsesControl';
import {useSelector} from 'react-redux';
import {useQualtrics} from '@ey/canvascoreservices/Qualtrics.js';

const MotifMenuOptions = (props) => {
	const {
		baseCanvasUrl,
		engagementId,
		engagementDescription,
		featureConfigurations,
		gaRoleTypeId,
		quickLinks,
		engagement,
		isOfflineMode
	} = props;

	const aresTaskBuildMilestoneId = useAresTaskBuildMilestoneId();
	const configurations = useSelector((state) => state.configurations);
	const paansuserdata = useSelector((state) => state.paansuserdata);

	const onOptionClick = useCallback(() => {
		window.dispatchEvent(new CustomEvent('closeMenu', {}));
	}, []);

	const isActiveSection = useCallback((elevatedSection) => {
		let pathname = window.location.pathname.substr(1);
		let firstSlashIndex = pathname.indexOf('/');
		let activeSection = firstSlashIndex > -1 ? pathname.substr(0, firstSlashIndex) : pathname;
		if (activeSection === elevatedSection) {
			return 'selected';
		}
	}, []);

	const itemTitle = useCallback((label) => {
		return label;
	}, []);

	const qualtricsProjectId = configurations?.features?.qualtrics?.qualtricsProjectId;
	const showQualtricsLink = qualtricsProjectId && configurations?.features?.qualtrics?.isQualtricsEnabled === true;
	const qualtricsInterceptAPI = useQualtrics(qualtricsProjectId);

	const onLeaveUsFeedbackClick = () => {
		qualtricsInterceptAPI((API) => {
			API.unload();
			API.load().done(runQualtricsIntercept);
		});
	};

	const runQualtricsIntercept = () => {
		qualtricsInterceptAPI((API) => {
			API.run();
		});
	};

	const renderQualtricsMotifLinkOption = useCallback((title) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
				}}
			>
				<Link onClick={onLeaveUsFeedbackClick}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const renderMotifLinkOption = useCallback((href, title, onClick) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
					onClick && onClick();
				}}
			>
				<Link to={href}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const renderExternalLinkOption = useCallback((href, title, className, onClick, target) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
					onClick && onClick();
				}}
			>
				<a href={href} className={className} target={target ? target : '_blank'}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</a>
			</MenuItem>
		);
	}, []);

	const renderGroup = useCallback((group) => {
		if (group.disabled) {
			return <StyledDisabledItem>{group.name}</StyledDisabledItem>;
		} else {
			let options = group.options.filter((option) => {
				return option.show === undefined || option.show;
			});

			return (
				<MenuItemGroup groupName={group.name} openButtonProps={group.openButtonProps}>
					{options?.length > 0 ? (
						<React.Fragment>
							{options.map((option) => {
								return renderOption(option);
							})}
						</React.Fragment>
					) : (
						<section className="motif-menu-list-item NotApplicableItem">
							<EllipseControl content={labels.navNotApplicable} tooltip={labels.navNotApplicable} noOfLines={1} />
						</section>
					)}
				</MenuItemGroup>
			);
		}
	}, []);

	const renderOption = useCallback(({className, disabled, title, href, onClick, render, target}) => {
		if (disabled) {
			return <StyledDisabledItem>{title}</StyledDisabledItem>;
		} else {
			if (href) {
				return (
					<MenuItem
						onClick={() => {
							onOptionClick();
							onClick && onClick();
						}}
					>
						<Link data-opennewtab to={href?.pathname} className={className} target={target} rel="noopener noreferrer">
							<EllipseControl content={title} tooltip={title} noOfLines={1} />
						</Link>
					</MenuItem>
				);
			} else {
				return render && render();
			}
		}
	}, []);

	const groupsConfig = useMemo(() => {
		const kronosBaseUrl = env.getURL('kronos_web');

		const showALRADocumentLink = quickLinks.accountLevelRiskAssessmentDocumentId !== null,
			showUnderstandITProcessLink = quickLinks.itFlowISA315DocumentId !== null;

		const alraDocumentUrl =
				showALRADocumentLink &&
				`fit?engagementid=${engagementId}&documentid=${quickLinks.accountLevelRiskAssessmentDocumentId}`,
			understandITProcessDocUrl =
				showUnderstandITProcessLink &&
				`fit?engagementid=${engagementId}&documentid=${quickLinks.itFlowISA315DocumentId}`;

		const alraSummaryUrl = `${kronosBaseUrl}/${alraDocumentUrl}`;
		const understandITProcessUrl = `${kronosBaseUrl}/${understandITProcessDocUrl}`;

		const showGroupStructureLink = quickLinks.hasGroupStructureSummary;
		const groupStructureUrl = quickLinks.groupStructureV2DocumentId
			? `${kronosBaseUrl}/fit?engagementid=${engagementId}&documentid=${quickLinks.groupStructureV2DocumentId}`
			: baseCanvasUrl + '#groupauditstructuresummary';

		const showGroupInstructionLink = quickLinks.hasGroupInstructionSummary;
		let groupInstructionUrl = quickLinks.groupInstructionV2DocumentId
			? `${kronosBaseUrl}/fit?engagementid=${engagementId}&documentid=${quickLinks.groupInstructionV2DocumentId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		const showGroupInvolvementLink = quickLinks.groupInvolvementV2DocumentId !== null;
		let groupInvolvementUrl = quickLinks.groupInvolvementV2DocumentId
			? `${kronosBaseUrl}/fit?engagementid=${engagementId}&documentid=${quickLinks.groupInvolvementV2DocumentId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		return [
			{
				name: itemTitle(labels.scopeAndStrategyTabLabel),
				openButtonProps: labels.scopeAndStrategyTabLabel,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasAccountSummary && quickLinks.accountLevelRiskAssessmentDocumentId === null,
						render: () =>
							renderExternalLinkOption(
								baseCanvasUrl + '#accountsummary',
								labels.navAccountSummary,
								undefined,
								undefined,
								'_blank'
							)
					},
					{
						show: showALRADocumentLink,
						render: () =>
							renderExternalLinkOption(
								alraSummaryUrl,
								itemTitle(labels.accountRiskAssessmentSummary),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: quickLinks.hasScotSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#scotsummary', labels.navScotSummary),
						target: '_blank'
					},
					{
						show: quickLinks.hasCRASummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#crasummary', labels.navCRAsummary),
						target: '_blank'
					},
					{
						show: quickLinks.hasITFlowISA315,
						render: () => renderExternalLinkOption(understandITProcessUrl, labels.understandITProcess),
						target: '_blank'
					},
					{
						show: quickLinks.hasItSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#itsummary', labels.navItSummary),
						target: '_blank'
					},
					{
						show: quickLinks.hasSubstantiveEvaluationMatrix,
						href: {
							pathname: `${smartScreenRoutes.sem}?engagementId=${engagementId}&documentid=${quickLinks.semDocumentId}`
						},
						className: isActiveSection(smartScreenRoutes.sem),
						title: itemTitle(labels.navSEM)
					},
					{
						show: quickLinks.hasAuditPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#plan', labels.navAuditplan),
						target: '_blank'
					},
					{
						show: quickLinks.hasWorkPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#plan', labels.navWorkplan),
						target: '_blank'
					}
				]
			},
			{
				name: itemTitle(labels.navConclusion),
				openButtonProps: labels.navConclusion,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasAuditPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#findingsHub', labels.navFindings),
						target: '_blank'
					},
					{
						show: quickLinks.hasModificationsAfterRRD && quickLinks.modifiedAfterRRDDocumentId,
						render: () =>
							renderMotifLinkOption(
								`/smartscreens/440gl?engagementid=${engagementId}&documentid=${quickLinks.modifiedAfterRRDDocumentId}`,
								labels.nav440GL
							),
						className: isActiveSection(smartScreenRoutes.fourFortyGl)
					},
					{
						show: quickLinks.hasIndependence,
						render: () =>
							renderMotifLinkOption(
								'/smartscreens/independence?engagementid=' +
									engagementId +
									'&documentid=' +
									quickLinks.independenceSummaryDocId,
								labels.navTeamMemberIndependence
							),
						className: isActiveSection(smartScreenRoutes.independence)
					},
					{
						href: {pathname: '/archivetimeline?engagementid=' + engagementId},
						className: isActiveSection('archivetimeline'),
						title: itemTitle(labels.navArchiveChecklist)
					}
				]
			},
			{
				name: itemTitle(labels.navGroupAudit),
				openButtonProps: labels.navGroupAudit,
				disabled: isOfflineMode,
				show: quickLinks.hasGroupInstructionSummary || quickLinks.hasGroupStructureSummary || gaRoleTypeId,
				options: [
					{
						show: showGroupStructureLink,
						render: () =>
							renderExternalLinkOption(
								groupStructureUrl,
								labels.navGroupStructureSummary,
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: showGroupInstructionLink,
						render: () =>
							renderExternalLinkOption(
								groupInstructionUrl,
								labels.navGroupInstructionSummary,
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: showGroupInvolvementLink,
						render: () =>
							renderExternalLinkOption(groupInvolvementUrl, labels.navGroupInvolvement, undefined, undefined, '_blank')
					},
					{
						show:
							(gaRoleTypeId === gaRoleTypes.primary || gaRoleTypeId === gaRoleTypes.component) &&
							engagement.isGroupAuditEngagement,
						render: () =>
							renderMotifLinkOption('/dashboard?engagementId=' + engagementId + '#groupStatus', labels.navGroupStatus)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderMotifLinkOption(
								'/dashboard?engagementId=' + engagementId + '#primaryGroupStatus',
								labels.navPrimaryStatus
							)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderMotifLinkOption(
								'/dashboard?engagementId=' + engagementId + '#componentGroupStatus',
								labels.navComponentStatus
							)
					},
					{
						render: () =>
							renderMotifLinkOption(
								'/dashboard?engagementId=' + engagementId + '#activityFeed',
								labels.navGroupActivityFeed
							)
					}
				]
			},
			{
				name: itemTitle(labels.navEngagementManagement),
				openButtonProps: labels.navEngagementManagement,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasV2Profile,
						render: () =>
							renderExternalLinkOption(
								env.getKronosUrl(
									'fit' + '?engagementid=' + engagementId + '&documentid=' + quickLinks.profileV2DocumentId
								),
								labels.navProfile
							),
						target: '_blank'
					},
					{
						show: quickLinks.hasV2Profile === false && quickLinks.hasV1Profile,
						render: () =>
							renderExternalLinkOption(
								baseCanvasUrl +
									`&engagementdesc=${encodeURIComponent(engagementDescription)}#engagementprofile/${
										quickLinks.profileV1DocumentId
									}/${documentTypes.PROFILE}&referrer=${encodeURI(window.location.href)}`,
								labels.navProfile
							)
					},
					{
						href: {pathname: '/manageteam?engagementid=' + engagementId},
						className: isActiveSection(smartScreenRoutes.independence),
						title: itemTitle(labels.navHeaderManageteam)
					},
					{
						href: {pathname: '/contentupdates?engagementid=' + engagementId},
						title: itemTitle(labels.navContentUpdates)
					},
					{
						href: {pathname: '/copyhub?engagementid=' + engagementId},
						title: itemTitle(labels.navCopyhub)
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports == false,
						render: () => renderExternalLinkOption(env.getURL('Zeus_web') + `/reportingHub`, labels.navReporting),
						title: itemTitle(labels.navReporting),
						target: '_blank'
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports == true,
						href: {pathname: '/reports?engagementid=' + engagementId},
						title: itemTitle(labels.navReporting)
					}
				]
			}
		];
	}, [isOfflineMode, quickLinks, gaRoleTypeId, engagement, engagementId, engagementDescription]);

	const optionsConfig = useMemo(() => {
		return [
			/*{
				show: aresTaskBuildMilestoneId,
				render: () =>
					renderExternalLinkOption(
						env.getAresUrl(featureConfigurations, engagementId),
						labels.guidedWorkflow,
						undefined,
						undefined,
						'_self'
					),
				title: itemTitle(labels.guidedWorkflow),
				target: '_blank',
				disabled: isOfflineMode
			},*/
			{
				show: featureConfigurations?.features?.hasHelixProjects,
				href: {pathname: `/connections?engagementId=${engagementId}`},
				title: itemTitle(labels.navHelixProjects),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.canvasEconomicsBase,
				render: () =>
					renderExternalLinkOption(featureConfigurations.features.canvasEconomicsBase, labels.navCanvasEconomics),
				title: itemTitle(labels.navCanvasEconomics),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.eyOversightBase,
				render: () => renderExternalLinkOption(featureConfigurations.features.eyOversightBase, labels.navOversight),
				title: itemTitle(labels.navOversight),
				disabled: isOfflineMode
			},
			{
				render: () => renderExternalLinkOption(env.getURL('help_web'), labels.navHelp),
				title: itemTitle(labels.navHelp),
				disabled: isOfflineMode,
				target: '_blank'
			},
			{
				render: () => renderExternalLinkOption(env.getURL('validation_help_web'), labels.validationNavHelp),
				title: itemTitle(labels.validationNavHelp),
				disabled: isOfflineMode,
				target: '_blank'
			},
			{
				show: paansuserdata?.hasAccepted && paansuserdata?.isPaansEnabled,
				render: () => renderExternalLinkOption(paansuserdata?.policyContentUrl, labels.privacyNotice),
				title: itemTitle(labels.privacyNotice),
				disabled: isOfflineMode,
				target: '_blank'
			},
			{
				show: showQualtricsLink,
				render: () => renderQualtricsMotifLinkOption(itemTitle(labels.leaveUsFeedback)),
				title: itemTitle(labels.leaveUsFeedback)
			}
		];
	}, [quickLinks, featureConfigurations, engagementId, isOfflineMode]);

	return (
		<React.Fragment>
			{groupsConfig.map((group) => {
				if (group.show === undefined || group.show) {
					return renderGroup(group);
				}
			})}
			{optionsConfig.map((option) => {
				if (option.show === undefined || option.show) {
					return renderOption(option);
				}
			})}
		</React.Fragment>
	);
};

export default MotifMenuOptions;

const StyledDisabledItem = styled.section`
	opacity: 0.5;
	pointer-events: none;
	height: var(--px-45);
	font-size: var(--px-16);
	padding: var(--px-15) 0 0 var(--px-19);
`;
